/**
 * useGenerationFileState
 *
 * Manages file/preview/product state + URL-param driven image loading
 * for GenerationFormV2. Max 50 lines.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { loadSourceImage, loadProductImage } from './imageLoader';

export function useGenerationFileState(sourceImageId?: string) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [productImageLoading, setProductImageLoading] = useState(!!sourceImageId);
  const [productInfo, setProductInfo] = useState<{ id: string; name: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const referencedAssetIdsRef = useRef<string[]>([]);

  const handleSuccess = useCallback((file: File, prev: string, info: { id: string; name: string }) => {
    setSelectedFile(file); setPreview(prev); setProductInfo(info); setProductImageLoading(false);
  }, []);

  const handleError = useCallback(() => {
    setProductInfo(null); setProductImageLoading(false);
  }, []);

  useEffect(() => {
    if (!sourceImageId) return;
    loadSourceImage(sourceImageId, handleSuccess, handleError);
  }, [sourceImageId, handleSuccess, handleError]);

  useEffect(() => {
    const productImageUrl = searchParams.get('productImageUrl');
    const productId = searchParams.get('productId');
    const productName = searchParams.get('productName');
    if (!productImageUrl || !productId) return;
    loadProductImage(productImageUrl, productId, productName || '', handleSuccess, handleError);
    const newParams = new URLSearchParams(searchParams);
    ['productImageUrl', 'productId', 'productName'].forEach(p => newParams.delete(p));
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, handleSuccess, handleError]);

  const clearFile = () => {
    setSelectedFile(null);
    setPreview(null);
    setProductInfo(null);
  };

  return { selectedFile, setSelectedFile, preview, setPreview, productImageLoading, productInfo, clearFile, referencedAssetIdsRef };
}
